<template>
  <div class="semantic-page">
    <!-- Hero with search -->
    <section class="semantic-hero">
      <div class="semantic-hero-inner">
        <h1 class="semantic-headline">Semantic Search</h1>
        <p class="semantic-subhead">
          Search vendor white papers, customer case studies, and analyst reports by meaning.
        </p>
        <div class="hero-search">
          <search-box autofocus />
        </div>
      </div>
    </section>

    <!-- Results -->
    <section class="semantic-results">
      <div class="results-header" v-if="query">
        <div class="results-count">
          <span v-if="loading">Searching</span>
          <span v-else-if="results.length === 0 && hasSearched">
            No results for <strong>{{ query }}</strong>
          </span>
          <span v-else>
            <strong>{{ results.length }}</strong> result{{ results.length === 1 ? '' : 's' }}
            for <strong>{{ query }}</strong>
          </span>
        </div>
        <div class="filter-row">
          <button
            v-for="t in contentTypeFilters"
            :key="t.value"
            class="filter-chip"
            :class="{ 'filter-chip--active': activeContentType === t.value }"
            @click="toggleContentType(t.value)"
          >
            {{ t.label }}
          </button>
        </div>
      </div>

      <div v-if="loading" class="results-loading">
        <div class="loading-spinner"></div>
      </div>

      <div v-else-if="results.length > 0" class="results-grid">
        <div
          v-for="r in filteredResults"
          :key="r.slug"
          class="result-card"
          @click="openResult(r)"
        >
          <div class="result-card-header">
            <span class="result-badge" :class="`result-badge--${r.content_type}`">
              {{ contentTypeLabel(r.content_type) }}
            </span>
            <span v-if="r.vendor" class="result-vendor">{{ r.vendor }}</span>
          </div>
          <h3 class="result-title">{{ r.title }}</h3>
          <p v-if="r.matching_chunks?.[0]?.text" class="result-excerpt">
            {{ excerpt(r.matching_chunks[0].text) }}
          </p>
          <div class="result-footer">
            <span v-if="r.publication_year" class="result-year">{{ r.publication_year }}</span>
            <span class="result-arrow">↗</span>
          </div>
        </div>
      </div>

      <div v-else-if="hasSearched && !loading" class="results-empty">
        <p>No matches in the curated content library.</p>
        <p class="results-empty-hint">
          Try a broader query, or
          <router-link to="/works">search academic papers</router-link>.
        </p>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useHead } from '@unhead/vue';
import SearchBox from '@/components/SearchBox.vue';

defineOptions({ name: 'SemanticPage' });

useHead({
  title: 'Semantic Search',
  titleTemplate: undefined,
});

const route = useRoute();

const ASK_API = (() => {
  if (typeof window === 'undefined') return '';
  if (window.location.hostname === 'localhost') return 'http://localhost:8091';
  return `${window.location.protocol}//${window.location.hostname}:8091`;
})();

const query = ref('');
const results = ref([]);
const loading = ref(false);
const hasSearched = ref(false);
const activeContentType = ref(null);

const contentTypeFilters = [
  { value: null, label: 'All' },
  { value: 'vendor_white_paper', label: 'White papers' },
  { value: 'case_study', label: 'Case studies' },
  { value: 'analyst_report', label: 'Analyst reports' },
];

const filteredResults = computed(() => {
  if (!activeContentType.value) return results.value;
  return results.value.filter(r => r.content_type === activeContentType.value);
});

function contentTypeLabel(t) {
  return ({
    vendor_white_paper: 'White paper',
    case_study: 'Case study',
    analyst_report: 'Analyst report',
    textbook_enrichment: 'Textbook',
  })[t] || t;
}

function excerpt(text) {
  if (!text) return '';
  // Strip the metadata header markdown if it leaked in
  let t = text.replace(/^#[^\n]*\n+/, '').replace(/^Vendor:[^\n]*\n+/m, '');
  t = t.trim();
  if (t.length > 280) t = t.slice(0, 280).replace(/\s+\S*$/, '') + '...';
  return t;
}

function toggleContentType(value) {
  activeContentType.value = activeContentType.value === value ? null : value;
}

async function runSearch(q) {
  if (!q || !q.trim()) {
    results.value = [];
    hasSearched.value = false;
    return;
  }
  loading.value = true;
  hasSearched.value = true;
  query.value = q.trim();

  try {
    const url = `${ASK_API}/api/semantic-search?q=${encodeURIComponent(q.trim())}&limit=30`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const data = await resp.json();
    results.value = data.results || [];
  } catch (e) {
    console.error('Semantic search failed:', e);
    results.value = [];
  } finally {
    loading.value = false;
  }
}

function openResult(r) {
  // Vendor papers & case studies have a source_url that points to the vendor landing page
  if (r.source_url) {
    window.open(r.source_url, '_blank', 'noopener');
    return;
  }
  // Textbook enrichments have openalex_id - link to the work page
  if (r.openalex_id) {
    window.open(`/works/${r.openalex_id}`, '_blank', 'noopener');
    return;
  }
}

// React to ?q= changes from the search box
watch(() => route.query.q, (q) => {
  runSearch(q || '');
}, { immediate: false });

onMounted(() => {
  if (route.query.q) {
    runSearch(route.query.q.toString());
  }
});
</script>

<style scoped lang="scss">
.semantic-page {
  font-family: 'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  min-height: 100vh;
  background: #ffffff;
}

// Hero
.semantic-hero {
  background: #041708;
  color: #ffffff;
  padding: 64px 24px 48px;
  text-align: center;
}

.semantic-hero-inner {
  max-width: 720px;
  margin: 0 auto;
}

.semantic-headline {
  font-family: 'Anton', sans-serif;
  font-size: 56px;
  font-weight: 400;
  text-transform: uppercase;
  letter-spacing: 0;
  line-height: 1.0;
  margin: 0 0 16px;
  color: #ffffff;
}

.semantic-subhead {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.75);
  margin: 0 0 32px;
}

.hero-search {
  margin-bottom: 16px;
}

// Results
.semantic-results {
  max-width: 1120px;
  margin: 0 auto;
  padding: 32px 24px 80px;
}

.results-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #e6e8e6;
}

.results-count {
  font-size: 14px;
  color: #4B5563;
}

.filter-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.filter-chip {
  background: #f4f4f5;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 500;
  color: #4B5563;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.15s;

  &:hover {
    background: rgba(210, 203, 254, 0.25);
    color: #041708;
  }
}

.filter-chip--active {
  background: #d2cbfe;
  border-color: #B8AFFE;
  color: #041708;
}

// Result grid
.results-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.result-card {
  background: #ffffff;
  border: 1px solid #e6e8e6;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.15s;
  display: flex;
  flex-direction: column;
  min-height: 180px;

  &:hover {
    border-color: #d2cbfe;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(107, 92, 231, 0.08);
  }
}

.result-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
  gap: 8px;
}

.result-badge {
  display: inline-block;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 3px 8px;
  border-radius: 4px;
  background: #d2cbfe;
  color: #041708;
  white-space: nowrap;
}

.result-badge--case_study {
  background: #cdfc8a;
}

.result-badge--analyst_report {
  background: #ffd9c6;
  color: #5c2606;
}

.result-vendor {
  font-size: 11px;
  font-weight: 500;
  color: #818982;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  text-align: right;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.result-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  color: #041708;
  margin: 0 0 8px;
}

.result-excerpt {
  font-size: 13px;
  line-height: 1.5;
  color: #4B5563;
  margin: 0 0 16px;
  flex: 1;
}

.result-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid #f4f4f5;
}

.result-year {
  font-size: 12px;
  color: #818982;
  font-weight: 500;
}

.result-arrow {
  font-size: 16px;
  color: #6B5CE7;
}

// Empty + loading states
.results-loading {
  display: flex;
  justify-content: center;
  padding: 80px 0;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f4f4f5;
  border-top-color: #6B5CE7;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.results-empty {
  text-align: center;
  padding: 64px 16px;
  color: #4B5563;

  p {
    margin: 0 0 8px;
  }
}

.results-empty-hint {
  font-size: 14px;
  color: #818982;

  a {
    color: #6B5CE7;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }
}

@media (max-width: 600px) {
  .semantic-headline {
    font-size: 36px;
  }
  .semantic-hero {
    padding: 40px 16px 32px;
  }
}
</style>
